import type { Editor } from '../core/Editor';
import type { Unsubscribe, BaseLayerData } from '../core/types';
import { resolveLayerAtTime } from '../core/effects/resolveLayer';
import { syncMediaElement } from './syncMediaElement';
import { mediaRegistry } from './mediaRegistry';

interface Entry {
  el: HTMLAudioElement;
  src: string;
}

interface MasterLock {
  layerId: string;
  el: HTMLMediaElement;
  layerStart: number;
  trimStart: number;
  /** el.currentTime when we locked in. */
  ctAtLock: number;
  /** playheadTime when we locked in. */
  playheadAtLock: number;
}

export class AudioReconciler {
  private editor: Editor;
  private entries = new Map<string, Entry>();
  private unsubscribe: Unsubscribe | null = null;
  private master: MasterLock | null = null;

  constructor(editor: Editor) {
    this.editor = editor;
    this.unsubscribe = editor.subscribe(() => this.tick());
    this.tick();
  }

  private tick(): void {
    const state = this.editor.getState();
    const interpolators = this.editor.getInterpolatorRegistry().getAll();
    const seen = new Set<string>();

    // 1) Manage audio elements (creation/disposal). Video elements are owned
    //    by VideoRenderer; we just drive their playback via the registry.
    for (const layer of Object.values(state.layers)) {
      if (layer.type !== 'audio') continue;
      seen.add(layer.id);

      const src = (layer.src as string) || '';
      let entry = this.entries.get(layer.id);

      if (!entry || entry.src !== src) {
        if (entry) this.disposeEntry(layer.id, entry);
        if (!src) {
          this.entries.delete(layer.id);
          continue;
        }
        const el = document.createElement('audio');
        el.crossOrigin = 'anonymous';
        el.preload = 'auto';
        el.src = src;
        el.addEventListener('loadedmetadata', () => this.tick(), { once: true });
        attachDebugListeners(el, `audio:${layer.id.slice(-6)}`);
        entry = { el, src };
        this.entries.set(layer.id, entry);
        mediaRegistry.set(layer.id, el);
      }
    }

    for (const [id, entry] of this.entries) {
      if (!seen.has(id)) {
        this.disposeEntry(id, entry);
        this.entries.delete(id);
      }
    }

    // 2) Drive playback synchronously for ALL media (audio + video). Doing
    //    this here — rather than in VideoRenderer.update which is gated by
    //    React's render cycle — guarantees pause/play hits both immediately.
    for (const layer of Object.values(state.layers)) {
      if (layer.type !== 'audio' && layer.type !== 'video') continue;
      const el = mediaRegistry.get(layer.id);
      if (!el) continue;
      const resolved = resolveLayerAtTime(layer, state.playheadTime, interpolators);
      syncMediaElement(el, {
        isPlaying: state.isPlaying,
        playheadTime: state.playheadTime,
        layerStart: layer.startTime,
        layerEnd: layer.endTime,
        trimStart: (resolved.trimStart as number) ?? 0,
        trimEnd: (resolved.trimEnd as number) ?? 0,
        loop: !!resolved.loop,
        volume: (resolved.volume as number) ?? 1,
        debugLabel: `${layer.type}:${layer.id.slice(-6)}`,
      });
    }

    this.updateClockMaster(state.layers, state.isPlaying, state.playheadTime, interpolators);
  }

  /**
   * Picks a media element to act as the authoritative clock during play.
   * Preference: audio layer in window > video layer in window. Once locked,
   * stays locked until isPlaying flips off, the layer goes out of window, the
   * src changes, or the element loses readiness.
   */
  private updateClockMaster(
    layers: Record<string, BaseLayerData>,
    isPlaying: boolean,
    playheadTime: number,
    interpolators: Map<string, any>,
  ): void {
    if (!isPlaying) {
      if (this.master) {
        this.editor.setExternalClockSource(null);
        this.master = null;
      }
      return;
    }

    // Check whether the current master is still valid.
    if (this.master) {
      const layer = layers[this.master.layerId];
      const stillValid =
        layer &&
        playheadTime >= layer.startTime &&
        playheadTime <= layer.endTime &&
        this.master.el.readyState >= 2;
      if (!stillValid) {
        this.editor.setExternalClockSource(null);
        this.master = null;
      }
    }

    if (this.master) return;

    // Find a candidate: prefer audio in window, else video in window.
    const candidates: BaseLayerData[] = [];
    for (const layer of Object.values(layers)) {
      if (layer.type !== 'audio' && layer.type !== 'video') continue;
      if (playheadTime < layer.startTime || playheadTime > layer.endTime) continue;
      candidates.push(layer);
    }
    candidates.sort((a, b) => {
      if (a.type === b.type) return a.startTime - b.startTime;
      return a.type === 'audio' ? -1 : 1;
    });

    for (const layer of candidates) {
      const el = mediaRegistry.get(layer.id);
      if (!el || el.readyState < 2) continue;
      const resolved = resolveLayerAtTime(layer, playheadTime, interpolators);
      const trimStart = (resolved.trimStart as number) ?? 0;
      this.master = {
        layerId: layer.id,
        el,
        layerStart: layer.startTime,
        trimStart,
        ctAtLock: el.currentTime,
        playheadAtLock: playheadTime,
      };
      const m = this.master;
      this.editor.setExternalClockSource(() => m.playheadAtLock + (m.el.currentTime - m.ctAtLock));
      if (typeof window !== 'undefined' && (window as any).__KINEMA_AUDIO_DEBUG) {
        console.log(`[clock] master locked: ${layer.type}:${layer.id.slice(-6)} ct=${m.ctAtLock.toFixed(3)} playhead=${m.playheadAtLock.toFixed(3)}`);
      }
      return;
    }
  }

  private disposeEntry(layerId: string, entry: Entry): void {
    entry.el.pause();
    entry.el.removeAttribute('src');
    entry.el.load();
    mediaRegistry.delete(layerId);
  }

  destroy(): void {
    this.unsubscribe?.();
    this.unsubscribe = null;
    this.editor.setExternalClockSource(null);
    this.master = null;
    for (const [id, entry] of this.entries) this.disposeEntry(id, entry);
    this.entries.clear();
  }
}

/** Logs media-element lifecycle events when window.__KINEMA_AUDIO_DEBUG is set. */
export function attachDebugListeners(el: HTMLMediaElement, label: string): void {
  const events = ['loadedmetadata', 'canplay', 'canplaythrough', 'playing', 'waiting', 'stalled', 'suspend', 'pause', 'ended', 'error'];
  for (const evt of events) {
    el.addEventListener(evt, () => {
      if (typeof window !== 'undefined' && window.__KINEMA_AUDIO_DEBUG) {
        console.log(`[evt ${label}] ${evt} ct=${el.currentTime.toFixed(3)} ready=${el.readyState} buffered=${rangesToString(el.buffered)}`);
      }
    });
  }
}

function rangesToString(r: TimeRanges): string {
  const parts: string[] = [];
  for (let i = 0; i < r.length; i++) parts.push(`[${r.start(i).toFixed(2)}-${r.end(i).toFixed(2)}]`);
  return parts.join(',') || '∅';
}
